import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import InfiniteScroll from 'react-infinite-scroll-component';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { motion } from 'framer-motion';

const ALL_DISTRICTS = 'All Districts';

const sortByDate = (newsArray, sortOrder) => {
  return [...newsArray].sort((a, b) => {
    const diff = new Date(b.story_date) - new Date(a.story_date);
    return sortOrder === 'asc' ? -diff : diff;
  });
};

// Sorts by date first, then (if a specific district is selected) moves
// stories from that district to the front while keeping the rest of the
// districts filled in after, in the same date order.
const getFilteredSortedData = (newsArray, selectedDistrict, sortOrder) => {
  const sorted = sortByDate(newsArray, sortOrder);

  if (selectedDistrict === ALL_DISTRICTS) {
    return sorted;
  }

  const matching = sorted.filter((news) => news.school_district === selectedDistrict);
  const rest = sorted.filter((news) => news.school_district !== selectedDistrict);
  return [...matching, ...rest];
};

export default function Features({ newsData }) {
  const [visibleItems, setVisibleItems] = React.useState(8);
  const [hasMore, setHasMore] = React.useState(true);
  const [selectedDistrict, setSelectedDistrict] = React.useState(ALL_DISTRICTS);
  const [sortOrder, setSortOrder] = React.useState('desc');

  const districtOptions = React.useMemo(() => {
    const distinctDistricts = Array.from(
      new Set(newsData.map((news) => news.school_district).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
    return [ALL_DISTRICTS, ...distinctDistricts];
  }, [newsData]);

  const sortedNewsData = React.useMemo(
    () => getFilteredSortedData(newsData, selectedDistrict, sortOrder),
    [newsData, selectedDistrict, sortOrder]
  );

  React.useEffect(() => {
    setVisibleItems(8);
    setHasMore(true);
  }, [selectedDistrict, sortOrder]);

  const loadMoreItems = React.useCallback(() => {
    if (visibleItems >= sortedNewsData.length) {
      setHasMore(false);
      return;
    }
    setVisibleItems(prev => prev + 3);
  }, [visibleItems, sortedNewsData.length]);

  const displayedItems = sortedNewsData.slice(0, visibleItems);

  return (
    <Container id="features" sx={{ pt: { xs: 14, sm: 16 }, pb: { xs: 8, sm: 16 }, display: 'flex', justifyContent: 'center'}}>
      <Grid container spacing={6} justifyContent="center">
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'center',
              gap: 2,
              mb: 3,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="district-filter-label">School District</InputLabel>
              <Select
                labelId="district-filter-label"
                label="School District"
                value={selectedDistrict}
                onChange={(e) => setSelectedDistrict(e.target.value)}
              >
                {districtOptions.map((district) => (
                  <MenuItem key={district} value={district}>
                    {district}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="date-sort-label">Date</InputLabel>
              <Select
                labelId="date-sort-label"
                label="Date"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <MenuItem value="desc">Newest first</MenuItem>
                <MenuItem value="asc">Oldest first</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <InfiniteScroll
            dataLength={displayedItems.length}
            next={loadMoreItems}
            hasMore={hasMore}
            loader={
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70px' }}>
                <CircularProgress />
              </Box>
            }
            endMessage={
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50px' }}>
                <Typography variant="h5" color="text.secondary">
                  All caught up!
                </Typography>
              </Box>
            }
            style={{ paddingBottom: '10px' }}
          >
            <Grid container spacing={3} direction="column" alignItems="center" sx={{ pt: { xs: 3, sm: 4 } }}>
              {displayedItems.map((news) => (
                <Grid item xs={12} key={news.news_id}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <motion.div
                      style={{ width: '100%', maxWidth: 700 }}
                      whileHover={{ scale: 1.0075, y: -1 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                    >
                      <Card
                        variant="outlined"
                        sx={{
                          width: '100%',
                          cursor: 'pointer',
                          borderColor: 'transparent',
                          '&:hover': {
                            boxShadow: '0px 4px 14px rgba(66, 165, 245, 0.6)',
                            backgroundColor: 'rgba(66, 165, 245, 0.08)',
                            borderColor: 'rgba(66, 165, 245, 0.5)',
                          },
                        }}
                        onClick={() => window.open(news.url_link || '#', '_blank')}
                      >
                        <Box p={1.5}>
                          <Typography
                            variant="h6"
                            color="text.primary"
                            sx={{
                              fontSize: '1rem',
                              display: '-webkit-box',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 2,
                              overflow: 'hidden',
                            }}
                          >
                            {news.title || 'Title not available'}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              my: 0.5,
                              fontSize: '0.875rem',
                              display: '-webkit-box',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 3,
                              overflow: 'hidden',
                            }}
                          >
                            {news.short_summary || 'Summary not available'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.75rem' }}>
                            {news.story_date ? new Date(news.story_date).toLocaleDateString() : 'Date not available'}
                          </Typography>
                        </Box>
                      </Card>
                    </motion.div>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </InfiniteScroll>
        </Grid>
      </Grid>
    </Container>
  );
}
